export type WorkforceAuthLink =
  | { readonly kind: "token"; readonly type: "invite" | "recovery"; readonly tokenHash: string }
  | {
      readonly kind: "session";
      readonly type: "invite" | "recovery";
      readonly accessToken: string;
      readonly refreshToken: string;
    }
  | { readonly kind: "invalid" };

const authParameters = [
  "token_hash",
  "token",
  "type",
  "access_token",
  "refresh_token",
  "expires_at",
  "expires_in",
  "token_type",
  "error",
  "error_code",
  "error_description",
  "code",
  "sb",
];

/** Parse only; verifying a single-use token must wait for an explicit user action. */
export function readWorkforceAuthLink(href: string): WorkforceAuthLink | null {
  const url = new URL(href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const params = authParameters.some((key) => fragment.has(key)) ? fragment : url.searchParams;
  if (!authParameters.some((key) => params.has(key))) return null;
  const type = params.get("type");
  if (
    params.has("error") ||
    params.has("error_code") ||
    (type !== "invite" && type !== "recovery")
  ) {
    return { kind: "invalid" };
  }
  if (authParameters.some((key) => params.getAll(key).length > 1)) return { kind: "invalid" };
  const tokenHash = params.get("token_hash");
  if (tokenHash && /^(?:pkce_)?[a-f0-9]{56,128}$/i.test(tokenHash) && !params.has("access_token")) {
    return { kind: "token", type, tokenHash };
  }
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  if (
    !tokenHash &&
    accessToken &&
    refreshToken &&
    accessToken.length <= 8192 &&
    refreshToken.length <= 8192
  ) {
    return { kind: "session", type, accessToken, refreshToken };
  }
  return { kind: "invalid" };
}

/** Keep credentials out of history/referrers; retain unrelated navigation parameters. */
export function clearWorkforceAuthUrl(): void {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const authFragment = authParameters.some((key) => fragment.has(key));
  for (const key of authParameters) {
    url.searchParams.delete(key);
    if (authFragment) fragment.delete(key);
  }
  if (authFragment) url.hash = fragment.toString();
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}
